/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {FocusEventHandler, InputHTMLAttributes, KeyboardEventHandler, useEffect, useRef, useState} from 'react';
import {ChangeFunction, ErrorType, generateComponentId, OrnamentShape, roundTo} from '@dvrd/dvr-controls';
import {BolmateInput} from "../index";
import {isPasting, positionIsAfterComma, textIsSelected} from "../../../util/inputUtil";

interface Props {
    onChange: ChangeFunction<string | number>;
    onFocus?: FocusEventHandler;
    onBlur?: FocusEventHandler;
    onKeyDown?: KeyboardEventHandler;
    onEnter?: KeyboardEventHandler;
    value: string | number;
    label?: string;
    ornaments?: OrnamentShape | OrnamentShape[];
    inputProps?: InputHTMLAttributes<any>;
    disabled?: boolean;
    autoFocus?: boolean;
    autoSelect?: boolean;
    className?: string;
    inputClassName?: string;
    labelClassName?: string;
    ornamentClassName?: string;
    errorClassName?: string;
    error?: ErrorType;
    id?: string;
    wholeNumbers?: boolean;
    asNumber?: boolean;
    asCurrency?: boolean;
    disableSymbols?: boolean;
    unControlled?: boolean;
}

export default function BolmateNumberInput(props: Props) {
    const {
        disableSymbols, asNumber, value, onChange, onKeyDown, asCurrency, wholeNumbers, inputProps, unControlled
    } = props;
    const [innerValue, setInnerValue] = useState(value);
    const id = useRef(generateComponentId(props.id));

    function _onKeyDown(evt: React.KeyboardEvent<HTMLInputElement>) {
        const {key} = evt,
            numValue = Number(value);
        // Allow only number and simple edit keys
        const editKeys = /\d|Backspace|ArrowRight|ArrowLeft|ArrowUp|ArrowDown|Escape|Esc|Enter|[,.\-+]|Tab/;
        if (!editKeys.test(key) && !isPasting(evt))
            evt.preventDefault();

        // Handle Arrow events
        if (['ArrowUp', 'ArrowDown'].includes(key)) {
            evt.preventDefault();
            if (!isNaN(numValue))
                return onArrowEvent(evt, numValue);
        }

        // Handle event if no selected text
        onKeyDownSelected(evt);

        if (onKeyDown)
            onKeyDown(evt);
    }

    function onArrowEvent(evt: React.KeyboardEvent<HTMLInputElement>, numValue: number) {
        const {key} = evt,
            step = Number(inputProps?.step || asCurrency ? .01 : 1);
        if (key === 'ArrowUp')
            numValue += step;
        else if (key === 'ArrowDown')
            numValue -= step;
        let stringValue = numValue.toString();
        if (asCurrency) stringValue = roundTo(numValue, 2);
        if (asNumber) change(Number(stringValue));
        else change(stringValue);
    }

    function onKeyDownSelected(evt: React.KeyboardEvent<HTMLInputElement>) {
        if (textIsSelected(evt)) return;
        const {currentTarget, key} = evt,
            position = currentTarget.selectionStart;
        if (/[,.]/.test(key) && /[,.]/.test(value.toString()) && asCurrency)
            // Prevent double points or commas
            evt.preventDefault();

        if (wholeNumbers && /[.,]/.test(key))
            evt.preventDefault();
        else if (asCurrency && /[.,]\d{2}/.test(value.toString()) && /\d/.test(key) &&
            positionIsAfterComma(position, value.toString()))
            // Prevent more than 2 decimals when working with currencies
            evt.preventDefault();

        if (/[\-+]/.test(key) && (disableSymbols || (position && position !== 0)))
            // Prevent - or + sign completely or after first position
            evt.preventDefault();
    }

    function _onChange(value: string, evt: React.ChangeEvent<HTMLInputElement>) {
        value = value.replace(/ /g, '');
        if (!/^-?[\d.,]*$/.test(value)) evt.preventDefault();
        else {
            if (value.startsWith('.') || value.startsWith(',')) value = '0' + value;
            if (wholeNumbers)
                // Only allow numbers and minus sign for negative numbers
                value = value.replace(/[^0-9-]/g, '').trim();
            if (disableSymbols)
                value = value.replace(/[\-+]/g, '').trim();
            if (asNumber) {
                const numValue = Number(value.replace(',', '.'));
                change(numValue, evt);
            } else change(value, evt);
        }
        return value;
    }

    function change(value: number | string, evt?: React.ChangeEvent<HTMLInputElement>) {
        setInnerValue(value);
        onChange(value, evt);
    }

    function focusInput() {
        document.getElementById(id.current + '-input')?.focus();
    }

    useEffect(() => {
        if (!unControlled) setInnerValue(value);
    }, [value]);

    useEffect(() => {
        if (!props.disabled && props.autoFocus)
            focusInput();
    }, [props.disabled, props.autoFocus])

    return (
        <BolmateInput {...props} id={id.current} onChange={_onChange} onKeyDown={_onKeyDown} value={innerValue}/>
    );
}